import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Users, Mail, CreditCard, LayoutDashboard, LogOut, Menu, X, Video, GraduationCap, BookOpen, Award, TrendingUp, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getAdminProfile, getDashboardStats, getAdminLeads, updateAdminLead, getWebinarRegistrations, getAdminPayments } from '@/lib/api';
import AdminEnrollments from '@/pages/admin/AdminEnrollments';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navItems = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Students', href: '/admin/students', icon: GraduationCap },
    { name: 'Questions', href: '/admin/questions', icon: BookOpen },
    { name: 'Enrollments', href: '/admin/enrollments', icon: ListTodo },
    { name: 'Leads', href: '/admin/leads', icon: Users },
    { name: 'Webinars', href: '/admin/webinars', icon: Video },
    { name: 'Certifications', href: '/admin/certifications', icon: Award },
    { name: 'Payments', href: '/admin/payments', icon: CreditCard },
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-brand-900 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-brand-800">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-0.5">
              <img src="/brand/lq-symbol.png" alt="LearnQuanta" className="w-full h-full object-contain" />
            </div>
            <span className="text-white font-heading font-bold">LearnQuanta Admin</span>
          </Link>
          <button onClick={onClose} className="lg:hidden text-white"><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link key={item.name} to={item.href} onClick={onClose} className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.href ? 'bg-brand-700 text-white' : 'text-brand-200 hover:bg-brand-800 hover:text-white'}`}>
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-brand-800">
          <button onClick={handleLogout} className="flex items-center space-x-3 px-4 py-3 w-full text-brand-200 hover:bg-brand-800 hover:text-white rounded-lg">
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getDashboardStats();
        setStats(response.data);
      } catch (error) { console.error('Failed to fetch stats'); }
    };
    fetchStats();
  }, []);

  return (
    <div data-testid="admin-dashboard-home">
      <h1 className="text-2xl font-bold text-brand-900 mb-6">Dashboard Overview</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: 'Total Leads', value: stats?.total_leads || 0, icon: Users },
          { title: 'New Leads', value: stats?.new_leads || 0, icon: Mail },
          { title: 'Webinar Registrations', value: stats?.webinar_registrations || 0, icon: Video },
          { title: 'Total Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: CreditCard },
        ].map((stat) => (
          <Card key={stat.title}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.title}</p>
                  <p className="text-2xl font-bold text-brand-900">{stat.value}</p>
                </div>
                <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-brand-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <InvestorMemoPanel />
    </div>
  );
};

const InvestorMemoPanel = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [busy, setBusy] = useState(false);

  const downloadFile = async (path, filename) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const openHtmlPreview = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/investor/memo.html`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) toast.error('Pop-up blocked — allow pop-ups to preview the memo');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-8 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="investor-memo-panel">
      <CardContent className="pt-6 pb-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-brand-900">Investor Memo — Fundraising Pack</h3>
              <span className="text-[10px] uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold">Confidential</span>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Banking-grade Investment Memorandum auto-generated from the live product. Polished HTML for browser preview (Cmd/Ctrl-P → Save as PDF), plus editable Markdown source.
            </p>
            <p className="text-xs text-slate-400 mt-1">Path: <code>/app/memory/INVESTMENT_MEMORANDUM.md</code> · Edit in repo to update.</p>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <Button
              onClick={openHtmlPreview}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="preview-memo-html-btn"
            >
              Preview / Save as PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadFile('/api/admin/investor/memo.md', 'LearnQuanta_Investment_Memorandum.md')}
              disabled={busy}
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="download-memo-md-btn"
            >
              Download .md
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLeads();
  }, [filter]);

  const fetchLeads = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await getAdminLeads(params);
      setLeads(response.data);
    } catch (error) { console.error('Failed to fetch leads'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await updateAdminLead(id, { status });
      toast.success('Lead status updated');
      fetchLeads();
    } catch (error) { toast.error('Failed to update lead'); }
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-brand-900">Leads Management</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell className="font-medium">{lead.first_name} {lead.last_name}</TableCell>
                <TableCell>{lead.email}</TableCell>
                <TableCell>{lead.phone || '-'}</TableCell>
                <TableCell><Badge variant="outline">{lead.source}</Badge></TableCell>
                <TableCell><Badge className={lead.status === 'new' ? 'bg-blue-100 text-blue-700' : lead.status === 'converted' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>{lead.status}</Badge></TableCell>
                <TableCell>{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Select value={lead.status} onValueChange={(val) => updateStatus(lead.id, val)}>
                    <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

const WebinarsPage = () => {
  const [registrations, setRegistrations] = useState([]);
  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getWebinarRegistrations();
        setRegistrations(response.data);
      } catch (error) { console.error('Failed to fetch registrations'); }
    };
    fetch();
  }, []);

  return (
    <div data-testid="admin-webinars-page">
      <h1 className="text-2xl font-bold text-brand-900 mb-6">Webinar Registrations</h1>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Topic</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {registrations.map((reg) => (
              <TableRow key={reg.id}>
                <TableCell className="font-medium">{reg.first_name} {reg.last_name}</TableCell>
                <TableCell>{reg.email}</TableCell>
                <TableCell>{reg.company || '-'}</TableCell>
                <TableCell>{reg.webinar_topic}</TableCell>
                <TableCell>{new Date(reg.registered_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getAdminPayments();
        setPayments(response.data);
      } catch (error) { console.error('Failed to fetch payments'); }
    };
    fetch();
  }, []);

  return (
    <div data-testid="admin-payments-page">
      <h1 className="text-2xl font-bold text-brand-900 mb-6">Payments</h1>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Program</TableHead>
              <TableHead>Track</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell className="font-medium">{payment.customer_name}</TableCell>
                <TableCell>{payment.customer_email}</TableCell>
                <TableCell>{payment.erp_program?.toUpperCase()}</TableCell>
                <TableCell>{payment.training_track}</TableCell>
                <TableCell className="font-mono">${payment.amount}</TableCell>
                <TableCell><Badge className={payment.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{payment.payment_status}</Badge></TableCell>
                <TableCell>{new Date(payment.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ==================== STUDENTS MANAGEMENT ====================

const StudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStudents();
    fetchStats();
  }, []);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStudents(data.students || []);
      }
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/students/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const updatePlan = async (studentId, newPlan) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/students/${studentId}/plan?plan=${newPlan}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Plan updated successfully');
        fetchStudents();
      } else {
        toast.error('Failed to update plan');
      }
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const deleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student and all their data?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/students/${studentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Student deleted');
        fetchStudents();
      } else {
        toast.error('Failed to delete student');
      }
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  return (
    <div data-testid="admin-students">
      <h1 className="text-2xl font-bold text-brand-900 mb-6">Student Management</h1>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-brand-900">{stats.total_students}</div>
              <p className="text-sm text-slate-500">Total Students</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-violet-600">{stats.by_plan?.pro || 0}</div>
              <p className="text-sm text-slate-500">Pro Subscribers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.accuracy_rate}%</div>
              <p className="text-sm text-slate-500">Avg. Accuracy</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.pass_rate}%</div>
              <p className="text-sm text-slate-500">Sim Pass Rate</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Students Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Questions</TableHead>
              <TableHead>Readiness</TableHead>
              <TableHead>Simulations</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow>
            ) : students.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No students yet</TableCell></TableRow>
            ) : (
              students.map((student) => (
                <TableRow key={student.id}>
                  <TableCell className="font-medium">{student.first_name} {student.last_name}</TableCell>
                  <TableCell>{student.email}</TableCell>
                  <TableCell>
                    <Select value={student.plan} onValueChange={(val) => updatePlan(student.id, val)}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="team">Team</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{student.progress?.questions_answered || 0}</TableCell>
                  <TableCell>
                    <span className={`font-medium ${(student.progress?.readiness_score || 0) >= 70 ? 'text-green-600' : 'text-amber-600'}`}>
                      {student.progress?.readiness_score || 0}%
                    </span>
                  </TableCell>
                  <TableCell>{student.progress?.completed_simulations || 0}</TableCell>
                  <TableCell>{new Date(student.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button variant="destructive" size="sm" onClick={() => deleteStudent(student.id)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ==================== QUESTIONS MANAGEMENT ====================

const QuestionsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/questions/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      toast.error('Failed to load question stats');
    } finally {
      setLoading(false);
    }
  };

  const domainNames = {
    ai_workloads: 'AI Workloads & Considerations',
    ml_fundamentals: 'Machine Learning Fundamentals',
    computer_vision: 'Computer Vision',
    nlp: 'Natural Language Processing',
    generative_ai: 'Generative AI'
  };

  const domainWeights = {
    ai_workloads: 20,
    ml_fundamentals: 25,
    computer_vision: 15,
    nlp: 25,
    generative_ai: 15
  };

  return (
    <div data-testid="admin-questions">
      <h1 className="text-2xl font-bold text-brand-900 mb-6">Question Bank</h1>
      
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          {/* Summary Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>AI-900 Question Bank</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-brand-900 mb-2">{stats?.total_questions || 0}</div>
              <p className="text-slate-500">Total Questions</p>
            </CardContent>
          </Card>

          {/* By Domain */}
          <h2 className="text-lg font-semibold mb-4">Questions by Domain</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {Object.entries(stats?.by_domain || {}).map(([domain, count]) => (
              <Card key={domain}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-2xl font-bold text-brand-900">{count}</div>
                      <p className="text-sm text-slate-500">{domainNames[domain] || domain}</p>
                    </div>
                    <Badge className="bg-violet-100 text-violet-700">{domainWeights[domain] || 0}%</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* By Difficulty */}
          <h2 className="text-lg font-semibold mb-4">Questions by Difficulty</h2>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(stats?.by_difficulty || {}).map(([diff, count]) => (
              <Card key={diff}>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-brand-900">{count}</div>
                  <p className="text-sm text-slate-500">
                    {diff === '1' ? 'Easy' : diff === '2' ? 'Medium' : 'Hard'}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CERT_LABELS = {
  'ai-900': 'AI-900 Azure AI Fundamentals',
  'ai-102': 'AI-102 Azure AI Engineer',
  'az-900': 'AZ-900 Azure Fundamentals',
  'dp-100': 'DP-100 Azure Data Scientist',
  'ab-900': 'AB-900 Copilot & Agent Admin',
};

const CertificationsPage = () => {
  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const headers = { Authorization: `Bearer ${token}` };
        const [s, p] = await Promise.all([
          fetch(`${API_URL}/api/admin/cert-purchases/stats`, { headers }).then((r) => r.json()),
          fetch(`${API_URL}/api/admin/cert-purchases?limit=100`, { headers }).then((r) => r.json()),
        ]);
        setStats(s);
        setPurchases(p.purchases || []);
      } catch (e) {
        toast.error('Failed to load cert analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="text-slate-500" data-testid="cert-loading">Loading certification analytics...</div>;
  }

  return (
    <div data-testid="admin-certifications-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-brand-900">Certification Revenue</h1>
        <Badge className="bg-violet-100 text-violet-700">Per-cert paywall ($39-$49 each)</Badge>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { title: 'Total Cert Sales', value: stats?.total_purchases || 0, icon: Award, color: 'bg-violet-100 text-violet-700' },
          { title: 'Cert Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: CreditCard, color: 'bg-green-100 text-green-700' },
          { title: 'Checkout Initiated', value: stats?.total_initiated || 0, icon: TrendingUp, color: 'bg-blue-100 text-blue-700' },
          { title: 'Conversion Rate', value: `${stats?.conversion_rate || 0}%`, icon: TrendingUp, color: 'bg-amber-100 text-amber-700' },
        ].map((stat) => (
          <Card key={stat.title}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.title}</p>
                  <p className="text-2xl font-bold text-brand-900" data-testid={`cert-stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>{stat.value}</p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Revenue by Certification</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Certification</TableHead>
                <TableHead className="text-right">Initiated</TableHead>
                <TableHead className="text-right">Purchases</TableHead>
                <TableHead className="text-right">Conversion</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(stats?.by_cert || []).length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No cert purchases yet. Free users will appear here once they convert.</TableCell></TableRow>
              ) : (
                stats.by_cert.map((row) => (
                  <TableRow key={row.cert_code} data-testid={`cert-row-${row.cert_code}`}>
                    <TableCell className="font-medium">{CERT_LABELS[row.cert_code] || row.cert_code}</TableCell>
                    <TableCell className="text-right">{row.initiated}</TableCell>
                    <TableCell className="text-right font-semibold">{row.purchases}</TableCell>
                    <TableCell className="text-right">{row.conversion_rate}%</TableCell>
                    <TableCell className="text-right font-mono">${row.revenue.toLocaleString()}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Cert Purchases</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Cert</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Purchased</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchases.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No purchases yet.</TableCell></TableRow>
              ) : (
                purchases.map((p) => (
                  <TableRow key={p.session_id || p.customer_email + p.cert_code}>
                    <TableCell className="font-medium">{p.customer_name || '-'}</TableCell>
                    <TableCell>{p.customer_email}</TableCell>
                    <TableCell>{(p.cert_code || '').toUpperCase()}</TableCell>
                    <TableCell className="text-right font-mono">${p.amount}</TableCell>
                    <TableCell>{p.purchased_at ? new Date(p.purchased_at).toLocaleString() : '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin/login');
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="lg:ml-64">
        <header className="bg-white border-b border-slate-200 px-4 py-4 flex items-center justify-between lg:justify-end">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden"><Menu className="w-6 h-6" /></button>
          <span className="text-sm text-slate-500">Admin Panel</span>
        </header>
        <main className="p-6">
          <Routes>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="students" element={<StudentsPage />} />
            <Route path="questions" element={<QuestionsPage />} />
            <Route path="enrollments" element={<AdminEnrollments />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="webinars" element={<WebinarsPage />} />
            <Route path="certifications" element={<CertificationsPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="*" element={<DashboardHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
